import { CalcMissionSession, CraMonth, DealRoom, ProjectContext, TreasuryProfile, UserProfile, Workspace } from '@shared/types';
import { getActiveProjects } from './projects';

const MISSIO_STORE_KEY = 'indxone.missio.v1';
const MISSIO_OWNER_BACKUP_KEY = 'indxone.missio.owner.backup.v1';
const MISSIO_FOCUS_KEY = 'indxone.missio.focus.v1';

export type MissioMissionStatus = 'prospect' | 'gagne' | 'en-cours' | 'facture';
export type MissioMissionReadiness = 'a-cadrer' | 'prete' | 'en-action' | 'a-facturer' | 'encaissee';

export interface MissioMissionRecord {
  id: string;
  clientNom: string;
  titre: string;
  notes: string;
  budgetHT: number;
  tjm: number;
  joursEstimes: number;
  statut: MissioMissionStatus;
  readiness: MissioMissionReadiness;
  workedDays: number;
  revenueRecognized: number;
  invoiceCount: number;
  invoiceDraftCount: number;
  invoiceSentCount: number;
  invoicePaidCount: number;
  invoiceTotal: number;
  paidTotal: number;
  nextAction: string;
  sourceSignals: string[];
  createdAt: string;
  updatedAt: string;
}

export interface MissioStorePayload {
  profil: {
    prenom: string;
    nom: string;
    email: string;
    societe: string;
    role: string;
  };
  settings: {
    tjm: number;
    jours: number;
    status: UserProfile['status'];
  };
  missions: MissioMissionRecord[];
}

export interface MissioPipelinePreview {
  missions: MissioMissionRecord[];
  counts: Record<MissioMissionStatus, number>;
  grouped: Record<MissioMissionStatus, MissioMissionRecord[]>;
}

export interface MissioInvoiceRecord {
  id: string;
  missionId: string | null;
  number: string;
  client: string;
  title: string;
  missionTitle: string;
  amount: number;
  amountHT?: number;
  amountTTC?: number;
  date: string;
  status: string;
}

export interface MissioLaunchOptions {
  missionId?: string | null;
  invoiceId?: string | null;
  tab?: 'dashboard' | 'pipeline' | 'facturation' | 'estimation' | 'profil';
  filter?: 'all' | 'prospect' | 'en-cours' | 'gagne' | 'facture';
}

export function buildMissioPayload(workspace: Workspace, session?: CalcMissionSession | null): MissioStorePayload {
  const profile = workspace.profile;
  const activeProjects = getActiveProjects(workspace.projects);
  const missions = activeProjects.map((project) => projectToMissioMission(project, workspace));

  if (session && session.projectId) {
    const targetIndex = missions.findIndex((mission) => mission.id === session.projectId);
    if (targetIndex >= 0) {
      const budgetHT = Math.round(session.simple.tjm * session.simple.days);
      const targetProject = activeProjects.find((project) => project.id === session.projectId);
      missions[targetIndex] = {
        ...missions[targetIndex],
        budgetHT,
        tjm: session.simple.tjm,
        joursEstimes: session.simple.days,
        notes: buildMissionNotes(targetProject, workspace, session),
      };
    }
  }

  return {
    profil: {
      prenom: profile.firstName,
      nom: profile.lastName,
      email: profile.email,
      societe: profile.company,
      role: profile.role,
    },
    settings: {
      tjm: profile.tjm,
      jours: profile.daysPerMonth,
      status: profile.status,
    },
    missions,
  };
}

export function buildMissioPipelinePreview(workspace: Workspace, session?: CalcMissionSession | null): MissioPipelinePreview {
  const missions = buildMissioPayload(workspace, session).missions;
  const grouped: Record<MissioMissionStatus, MissioMissionRecord[]> = {
    prospect: [],
    gagne: [],
    'en-cours': [],
    facture: [],
  };

  missions.forEach((mission) => {
    grouped[mission.statut].push(mission);
  });

  return {
    missions,
    grouped,
    counts: {
      prospect: grouped.prospect.length,
      gagne: grouped.gagne.length,
      'en-cours': grouped['en-cours'].length,
      facture: grouped.facture.length,
    },
  };
}

function projectToMissioMission(project: ProjectContext, workspace: Workspace): MissioMissionRecord {
  const linkedSession = workspace.calcMissionSessions.find((session) => session.projectId === project.id);
  const missionInvoices = readMissioInvoices().filter((invoice) => invoice.missionId === project.id);
  const budgetHT = linkedSession
    ? Math.round(linkedSession.simple.tjm * linkedSession.simple.days)
    : Math.round(project.budget || ((project.tjm || 0) * 10));
  const tjm = linkedSession?.simple.tjm || project.tjm || 0;
  const joursEstimes = linkedSession?.simple.days || inferDays(project, linkedSession);
  const craMonths = workspace.cras.filter((entry) => entry.projectId === project.id);
  const workedDays = craMonths.reduce((sum, month) => sum + (month.workedDays || 0), 0);
  const revenueRecognized = craMonths.reduce((sum, month) => sum + (month.revenue || 0), 0);
  const invoiceSummary = summarizeInvoices(missionInvoices);
  const readiness = resolveReadiness({
    statut: resolveMissioStatus(project, workspace),
    workedDays,
    revenueRecognized,
    invoiceDraftCount: invoiceSummary.draftCount,
    invoiceSentCount: invoiceSummary.sentCount,
    invoicePaidCount: invoiceSummary.paidCount,
  });
  const nextAction = resolveNextAction({
    readiness,
    invoiceDraftCount: invoiceSummary.draftCount,
    invoiceSentCount: invoiceSummary.sentCount,
    invoicePaidCount: invoiceSummary.paidCount,
    workedDays,
    revenueRecognized,
  });

  return {
    id: project.id,
    clientNom: project.client,
    titre: project.name,
    notes: buildMissionNotes(project, workspace, linkedSession),
    budgetHT,
    tjm,
    joursEstimes,
    statut: resolveMissioStatus(project, workspace),
    readiness,
    workedDays,
    revenueRecognized,
    invoiceCount: invoiceSummary.totalCount,
    invoiceDraftCount: invoiceSummary.draftCount,
    invoiceSentCount: invoiceSummary.sentCount,
    invoicePaidCount: invoiceSummary.paidCount,
    invoiceTotal: invoiceSummary.totalAmount,
    paidTotal: invoiceSummary.paidAmount,
    nextAction,
    sourceSignals: buildSourceSignals(linkedSession, workedDays, revenueRecognized, invoiceSummary.totalCount),
    createdAt: new Date(project.createdAt).toISOString(),
    updatedAt: new Date(project.updatedAt).toISOString(),
  };
}

function inferDays(project: ProjectContext, session?: CalcMissionSession | null): number {
  if (session?.simple.days) return session.simple.days;
  if (project.tjm && project.budget) return Math.round(project.budget / project.tjm);
  return 0;
}

function resolveMissioStatus(project: ProjectContext, workspace: Workspace): MissioMissionStatus {
  const treasury = workspace.treasuries.find((entry) => entry.projectId === project.id);
  const craMonths = workspace.cras.filter((entry) => entry.projectId === project.id);
  const dealRoom = workspace.dealRooms.find((entry) => entry.projectId === project.id);

  if (hasBillingSignal(treasury, craMonths)) return 'facture';
  if (hasExecutionSignal(dealRoom, craMonths)) return 'en-cours';
  if (dealRoom?.status === 'cadre') return 'gagne';
  return 'prospect';
}

function hasExecutionSignal(dealRoom?: DealRoom, craMonths: CraMonth[] = []): boolean {
  if (dealRoom?.status === 'en-cours') return true;
  return craMonths.some((month) => month.workedDays > 0 || month.revenue > 0);
}

function hasBillingSignal(treasury?: TreasuryProfile, craMonths: CraMonth[] = []): boolean {
  if (treasury && treasury.caYtd > 0) return true;
  return craMonths.some((month) => month.revenue > 0);
}

function buildMissionNotes(
  project?: ProjectContext,
  workspace?: Workspace,
  session?: CalcMissionSession | null,
): string {
  if (!project || !workspace) return session?.notes || '';

  const dealRoom = workspace.dealRooms.find((entry) => entry.projectId === project.id);
  const moscow = workspace.moscows.find((entry) => entry.projectId === project.id);
  const deliverables = dealRoom?.deliverables.slice(0, 3) || [];
  const priorities = [
    ...((moscow?.categories.must.items || []).slice(0, 2).map((item) => item.title)),
    ...((moscow?.categories.should.items || []).slice(0, 1).map((item) => item.title)),
  ].filter(Boolean);

  const parts = [
    project.description || project.scope,
    session?.notes,
    deliverables.length ? `Livrables : ${deliverables.join(', ')}` : '',
    priorities.length ? `Priorites : ${priorities.join(', ')}` : '',
  ].filter(Boolean);

  return parts.join('\n');
}

export function pushWorkspaceToMissio(workspace: Workspace, session?: CalcMissionSession | null): boolean {
  try {
    const payload = buildMissioPayload(workspace, session);
    localStorage.setItem(MISSIO_STORE_KEY, JSON.stringify(payload));
    localStorage.setItem(MISSIO_OWNER_BACKUP_KEY, JSON.stringify({
      prenom: workspace.profile.firstName,
      nom: workspace.profile.lastName,
      email: workspace.profile.email,
      societe: workspace.profile.company,
      role: workspace.profile.role,
      tjm: workspace.profile.tjm,
      jours: workspace.profile.daysPerMonth,
      status: workspace.profile.status,
      netTarget: workspace.profile.netTarget,
    }));
    return true;
  } catch {
    return false;
  }
}

export function prepareMissioLaunch(options: MissioLaunchOptions = {}): string {
  try {
    localStorage.setItem(MISSIO_FOCUS_KEY, JSON.stringify({
      missionId: options.missionId || null,
      invoiceId: options.invoiceId || null,
      tab: options.tab || 'pipeline',
      filter: options.filter || 'all',
      ts: Date.now(),
    }));
  } catch {
    // Ignore localStorage issues and still return a link
  }

  const params = new URLSearchParams();
  if (options.tab) params.set('tab', options.tab);
  if (options.missionId) params.set('mission', options.missionId);
  if (options.invoiceId) params.set('invoice', options.invoiceId);
  if (options.filter) params.set('filter', options.filter);
  const query = params.toString();
  return `/hub-app.html#/tools/missio${query ? `?${query}` : ''}`;
}

export function readMissioInvoices(): MissioInvoiceRecord[] {
  try {
    const invoices = readMissioStoreRaw().invoices || [];

    return invoices.map((invoice) => ({
      id: String(invoice.id || ''),
      missionId: invoice.missionId ? String(invoice.missionId) : null,
      number: String(invoice.number || ''),
      client: String(invoice.client || ''),
      title: String(invoice.titre || invoice.missionTitre || ''),
      missionTitle: String(invoice.missionTitre || invoice.titre || ''),
      amount: Number(invoice.montantTTC || invoice.montant || 0),
      amountHT: Number(invoice.montantHT || 0),
      amountTTC: Number(invoice.montantTTC || invoice.montant || 0),
      date: String(invoice.date || ''),
      status: String(invoice.statut || 'brouillon'),
    })).filter((invoice) => invoice.id);
  } catch {
    return [];
  }
}

export function createMissioDraftInvoice(options: {
  mission: MissioMissionRecord;
  profile: UserProfile;
}) {
  const store = readMissioStoreRaw();
  const today = new Date().toISOString().slice(0, 10);
  const nextNumber = `INV-${today.replace(/-/g, '')}-${String((store.invoices?.length || 0) + 1).padStart(3, '0')}`;
  const subtotal = Math.round(options.mission.budgetHT || options.mission.tjm * options.mission.joursEstimes || 0);
  const tvaRate = options.profile.status === 'ei' ? 0 : 0.2;
  const total = Math.round(subtotal * (1 + tvaRate));

  const invoice = {
    id: `inv_${Date.now()}`,
    missionId: options.mission.id,
    number: nextNumber,
    client: options.mission.clientNom || options.profile.company || '',
    titre: options.mission.titre,
    missionTitre: options.mission.titre,
    montantHT: subtotal,
    montantTTC: total,
    montant: total,
    tvaRate,
    date: today,
    statut: 'brouillon',
    notes: `Facture creee depuis le Hub pour ${options.mission.titre}.`,
    items: [
      {
        label: options.mission.titre,
        qty: Math.max(1, options.mission.joursEstimes || 1),
        unitPrice: Math.round(options.mission.tjm || subtotal || 0),
        total: subtotal,
      },
    ],
  };

  const nextStore = {
    ...store,
    invoices: [invoice, ...(store.invoices || [])],
  };
  localStorage.setItem(MISSIO_STORE_KEY, JSON.stringify(nextStore));

  return invoice;
}

export function updateMissioInvoiceStatus(invoiceId: string, status: 'brouillon' | 'envoyee' | 'payee') {
  const store = readMissioStoreRaw();
  const invoices = Array.isArray(store.invoices) ? store.invoices : [];
  const nextInvoices = invoices.map((invoice) => {
    if (String(invoice.id || '') !== invoiceId) return invoice;
    return {
      ...invoice,
      statut: status === 'envoyee' ? 'envoyée' : status === 'payee' ? 'payée' : 'brouillon',
    };
  });

  localStorage.setItem(MISSIO_STORE_KEY, JSON.stringify({
    ...store,
    invoices: nextInvoices,
  }));
}

function readMissioStoreRaw(): { invoices?: Array<Record<string, unknown>> } {
  const raw = localStorage.getItem(MISSIO_STORE_KEY);
  if (!raw) return {};
  const parsed = JSON.parse(raw) as { invoices?: Array<Record<string, unknown>> } | null;
  return parsed || {};
}

function normalizeInvoiceStatus(status: string) {
  const normalized = String(status || '').toLowerCase();
  if (normalized === 'envoyée' || normalized === 'envoyee') return 'envoyee';
  if (normalized === 'payée' || normalized === 'payee') return 'payee';
  return 'brouillon';
}

function summarizeInvoices(invoices: MissioInvoiceRecord[]) {
  const draftCount = invoices.filter((invoice) => normalizeInvoiceStatus(invoice.status) === 'brouillon').length;
  const sentCount = invoices.filter((invoice) => normalizeInvoiceStatus(invoice.status) === 'envoyee').length;
  const paidCount = invoices.filter((invoice) => normalizeInvoiceStatus(invoice.status) === 'payee').length;
  const totalAmount = invoices.reduce((sum, invoice) => sum + (invoice.amount || 0), 0);
  const paidAmount = invoices
    .filter((invoice) => normalizeInvoiceStatus(invoice.status) === 'payee')
    .reduce((sum, invoice) => sum + (invoice.amount || 0), 0);

  return {
    totalCount: invoices.length,
    draftCount,
    sentCount,
    paidCount,
    totalAmount,
    paidAmount,
  };
}

function resolveReadiness(input: {
  statut: MissioMissionStatus;
  workedDays: number;
  revenueRecognized: number;
  invoiceDraftCount: number;
  invoiceSentCount: number;
  invoicePaidCount: number;
}): MissioMissionReadiness {
  if (input.invoicePaidCount > 0) return 'encaissee';
  if (input.invoiceDraftCount > 0 || input.invoiceSentCount > 0) return 'a-facturer';
  if (input.revenueRecognized > 0 || input.workedDays > 0 || input.statut === 'en-cours') return 'en-action';
  if (input.statut === 'gagne') return 'prete';
  return 'a-cadrer';
}

function resolveNextAction(input: {
  readiness: MissioMissionReadiness;
  invoiceDraftCount: number;
  invoiceSentCount: number;
  invoicePaidCount: number;
  workedDays: number;
  revenueRecognized: number;
}) {
  if (input.readiness === 'encaissee') return 'Verifier la marge et preparer la suite.';
  if (input.invoiceSentCount > 0) return 'Relancer ou suivre le paiement.';
  if (input.invoiceDraftCount > 0) return 'Finaliser et envoyer la facture.';
  if (input.readiness === 'a-facturer') return 'Créer la facture de cette mission.';
  if (input.readiness === 'en-action') return input.workedDays > 0 ? 'Continuer l execution et preparer la facturation.' : 'Poser les prochains jours a produire.';
  if (input.readiness === 'prete') return 'Valider le demarrage ou creer la facture.';
  return 'Clarifier le besoin et cadrer le projet.';
}

function buildSourceSignals(session: CalcMissionSession | undefined, workedDays: number, revenueRecognized: number, invoiceCount: number) {
  const signals: string[] = [];
  if (session) signals.push('calc');
  if (workedDays > 0) signals.push('cra');
  if (revenueRecognized > 0) signals.push('ca');
  if (invoiceCount > 0) signals.push('factures');
  return signals;
}
